import { On3IconAthleteVerified } from '@on3/icons';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { getNILProfileLink } from 'components/Nil/helpers';
import { StarRating } from 'components/UI/StarRating/StarRating';
import useAthlete from 'contexts/AthleteProvider';
import Link from 'next/link';
import { useMemo } from 'react';

import styles from './CompactPlayer.module.scss';

interface ICompactPlayerProps {
  className?: string;
}
export const CompactPlayer = ({ className }: ICompactPlayerProps) => {
  const { athlete } = useAthlete();
  const { type, decommittedAsset, committedAssetRes } =
    athlete?.playerStatus ?? {};
  const isVerified = athlete?.athleteVerified;
  const nilValue = athlete?.nilValue ? (
    <span>{formatCurrency(athlete?.nilValue)}</span>
  ) : (
    <span>undisclosed</span>
  );

  const profileLink = getNILProfileLink({
    sport: athlete?.defaultSport?.name,
    slug: athlete?.slug,
  });

  const statusLogo = useMemo(() => {
    if (type === 'None') {
      return;
    }

    if (type === 'Decommitted') {
      return decommittedAsset?.defaultAsset;
    }

    return committedAssetRes;
  }, [committedAssetRes, decommittedAsset?.defaultAsset, type]);

  return (
    <div className={clsx(styles.block, className)}>
      <section className={styles.sectionWrapper}>
        <header data-name="name">
          <h2>{athlete?.name ?? ''}</h2>

          {isVerified && <On3IconAthleteVerified />}
        </header>
        <figure data-name="avatar">
          <On3Avatar
            {...athlete?.defaultAsset}
            fit="cover"
            height={80}
            quality={90}
            width={80}
          />
        </figure>
        {statusLogo && (
          <div className={styles.container} data-name="status">
            <figure>
              <On3Avatar {...statusLogo} height={20} teamAvatar width={20} />
            </figure>
            <span>{type}</span>
          </div>
        )}
        <div className={styles.container} data-name="classyear">
          <div className={styles.label} data-type="desktop">
            Class
          </div>
          <span>{athlete?.classYear ?? '-'}</span>
        </div>
        <div className={styles.wrapper} data-name="measurements">
          <div className={styles.container} data-name="position">
            <span
              aria-label="Position"
              className={styles.label}
              data-type="desktop"
            >
              POS
            </span>
            <span>{athlete?.positionAbbreviation ?? '-'}</span>
          </div>
          <div className={styles.container} data-name="height">
            <span
              aria-label="Height"
              className={styles.label}
              data-type="desktop"
            >
              HT
            </span>
            <span>{athlete?.height ?? '-'}</span>
          </div>
          <div className={styles.container} data-name="weight">
            <span
              aria-label="Weight"
              className={styles.label}
              data-type="desktop"
            >
              WT
            </span>
            <span>{athlete?.weight ?? '-'}</span>
          </div>
        </div>

        <div className={styles.container} data-name="ranking">
          <div className={styles.label} data-type="desktop">
            Rivals Industry
          </div>
          <div className={styles.starRating}>
            <StarRating
              fiveStarPlus={athlete?.ranking?.fiveStarPlus}
              hasBorder={false}
              href={`/rivals/${athlete?.slug}/industry-comparison/`}
              isBoldRating={false}
              isConsensus
              sport={athlete?.defaultSport?.name}
              stars={athlete?.ranking?.stars}
              year={athlete?.classYear?.toString()}
            />
            <span>{athlete?.ranking?.rating?.toFixed(2)}</span>
          </div>
        </div>
        <div className={styles.container} data-name="nil">
          <div className={styles.label}>NIL</div>
          <Link href={profileLink}>{nilValue}</Link>
        </div>
        <div className={styles.wrapper} data-name="location">
          <div className={styles.container} data-name="highschool">
            <div className={styles.label} data-type="desktop">
              High School
            </div>

            <span>
              <Link href={`/high-school/${athlete?.highSchool?.urlSlug}/`}>
                {athlete?.highSchool?.name ?? '-'}
              </Link>
            </span>
          </div>
          <div className={styles.container} data-name="hometown">
            <div className={styles.label} data-type="desktop">
              Hometown
            </div>
            <span>{athlete?.hometownName ?? '-'}</span>
          </div>
        </div>
      </section>
    </div>
  );
};
